import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection, addDoc, increment, serverTimestamp } from 'firebase/firestore';

function getLocalContacts() {
  try { return JSON.parse(localStorage.getItem('nexora_local_contacts') || '{}'); } 
  catch (e) { return {}; }
}

let activeDialingInstance = null;

const stopDialingTone = () => {
  activeDialingInstance = null;
  if (window.nexoraDialingAudio) {
    try { window.nexoraDialingAudio.pause(); window.nexoraDialingAudio.currentTime = 0; } catch (e) {}
    window.nexoraDialingAudio = null;
  }
  try {
    const allAudios = document.querySelectorAll('audio');
    allAudios.forEach(a => { a.pause(); a.currentTime = 0; });
  } catch (e) {}
};

const CallScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const callData = location.state || {}; 

  const [callStatus, setCallStatus] = useState(
    callData.isIncoming ? 'Connecting...' : 'Calling...'
  );
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(callData.callType === 'audio');
  
  // Volume Control State (Default Ear-piece mode)
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [isScreenShared, setIsScreenShared] = useState(false);
  const [groupInfo, setGroupInfo] = useState({ name: '', photoURL: '' });
  const [speakingParticipants, setSpeakingParticipants] = useState([]);
  
  const [targetUserData, setTargetUserData] = useState(null);
  const [currentUid, setCurrentUid] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [roomName, setRoomName] = useState('');

  // Audio Call Multi-participant State
  const [participants, setParticipants] = useState([]);

  const [isMinimized, setIsMinimized] = useState(false);
  const [bubblePos, setBubblePos] = useState({ x: window.innerWidth - 85, y: window.innerHeight - 150 });

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [allUsersList, setAllUsersList] = useState([]);
  const [selectedInviteUids, setSelectedInviteUids] = useState([]);
  const [searchContactQuery, setSearchContactQuery] = useState('');

  const timerIntervalRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const activeCallIdRef = useRef(null);
  const isConnectedRef = useRef(false);
  const localStreamRef = useRef(null);
  const pcRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const getRoomId = (uid1, uid2) => [uid1, uid2].sort().join('_');

  const normalizePhone = (p) => {
    if (!p) return '';
    let d = p.replace(/\D/g, '');
    if (d.startsWith('880')) d = d.substring(3);
    if (d.startsWith('0')) d = d.substring(1);
    return `+880${d}`;
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const startDialingTone = () => {
    stopDialingTone();
    try {
      const audio = new Audio('/Dailing.mp3');
      audio.loop = true;
      audio.volume = isSpeakerOn ? 1.0 : 0.25;
      activeDialingInstance = audio;
      window.nexoraDialingAudio = audio;
      const p = audio.play();
      if (p !== undefined) {
        p.then(() => { if (activeDialingInstance !== audio) { audio.pause(); audio.currentTime = 0; } }).catch(() => {});
      }
    } catch (e) {}
  };

  const startTimer = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setCallDuration(0);
    timerIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  // ============================================
  // Call Log Generator (History & Unread Count)
  // ============================================
  const createCallLog = async (logType, durationSec) => {
    if (!currentUid || callData.isGroupCall) return; // For now, 1-to-1 call logs
    const targetId = callData.targetUid;
    const roomId = getRoomId(currentUid, targetId);
    if (!roomId) return;

    const cName = callData.callType === 'video' ? 'Video Call' : 'Voice Call';
    const textMsg = logType === 'missed_call' ? `Missed ${cName}` : `${cName} Ended (${formatTime(durationSec)})`;
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const callSessionId = callData.callId || `call_${roomId}`;
    const isReceiver = !!callData.isIncoming;
    const direction = isReceiver ? 'incoming' : 'outgoing';

    const logData = {
      senderId: currentUid,
      senderName: currentUserData?.fullName || 'User',
      senderPhoto: currentUserData?.photoURL || '',
      type: 'call_log',
      callType: callData.callType || 'audio',
      logType,
      text: textMsg,
      duration: durationSec,
      timeStr,
      createdAt: serverTimestamp()
    };

    const buildHistoryEntry = (ownerUid, otherUid, otherUserData, ownerDirection) => ({
      callType: callData.callType || 'audio',
      logType,
      text: textMsg,
      duration: durationSec,
      createdAt: serverTimestamp(),
      targetId: otherUid,
      targetUid: otherUid,
      targetName: otherUserData?.fullName || otherUserData?.phoneNumber || 'Unknown',
      targetPhone: normalizePhone(otherUserData?.phoneNumber || ''),
      targetPhoto: otherUserData?.photoURL || '',
      status: logType === 'missed_call' ? 'missed' : 'completed',
      direction: ownerDirection,
      callSessionId,
      ownerUid
    });

    const writeCallHistory = async (ownerUid, entry) => {
      if (!ownerUid) return;
      const historyDocId = `${callSessionId}_${logType}_${Date.now()}`;
      try {
        await setDoc(doc(db, 'users', ownerUid, 'callHistory', historyDocId), entry, { merge: true });
      } catch (e) {
        console.error('Call history write failed', e);
      }
    };

    try {
      await addDoc(collection(db, `chats/${roomId}/messages`), logData);
      const chatRef = doc(db, 'chats', roomId);
      const updates = {
        participants: [currentUid, targetId],
        lastMessage: textMsg,
        lastSenderId: currentUid,
        updatedAt: serverTimestamp(),
        [`unreadCount.${currentUid}`]: 0
      };

      if (logType === 'missed_call') {
        updates[`unreadCount.${targetId}`] = increment(1);
      }

      await setDoc(chatRef, updates, { merge: true });

      const receiverEntry = buildHistoryEntry(currentUid, targetId, targetUserData, direction);
      await writeCallHistory(currentUid, receiverEntry);

      const callerEntry = buildHistoryEntry(targetId, currentUid, currentUserData, isReceiver ? 'outgoing' : 'incoming');
      await writeCallHistory(targetId, callerEntry);
    } catch (e) {
      console.error('Call Log Error', e);
    }
  };

  const handleMissedCall = async () => {
    stopDialingTone();
    if (activeCallIdRef.current) {
      await updateDoc(doc(db, 'calls', activeCallIdRef.current), { status: 'missed' }).catch(()=>{});
      setTimeout(() => deleteDoc(doc(db, 'calls', activeCallIdRef.current)).catch(()=>{}), 1000);
    }
    await createCallLog('missed_call', 0);
    if (jitsiApiRef.current) { try { jitsiApiRef.current.executeCommand('hangup'); } catch(e){} }
    navigate('/dashboard');
  };

  // 60 Seconds Timeout Auto-Cut Logic
  useEffect(() => {
    let timeout;
    if (!callData.isIncoming) {
      timeout = setTimeout(() => {
        if (!isConnectedRef.current) {
          handleMissedCall();
        }
      }, 55000); // 55 seconds
    }
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    stopDialingTone();
    const userUid = localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid;
    if (!userUid) { navigate('/login'); return; }
    setCurrentUid(userUid);

    onSnapshot(collection(db, 'users'), (snapshot) => {
      const uList = [];
      const localContacts = getLocalContacts();
      snapshot.forEach(docSnap => {
        const uData = docSnap.data();
        if (uData.uid !== userUid) {
          if (callData.isGroupCall && callData.groupMembers) {
            if (callData.groupMembers.includes(uData.uid)) uList.push(uData);
          } else {
            const normP = normalizePhone(uData.phoneNumber);
            if (localContacts[normP]) uList.push(uData);
          }
        }
      });
      setAllUsersList(uList);
    });

    let unsubscribeCallDoc = null;

    const initCall = async () => {
      const myDoc = await getDoc(doc(db, 'users', userUid));
      if (myDoc.exists()) setCurrentUserData(myDoc.data());

      // MISSED CALL BUG FIX: Check if incoming call is stale
      if (callData.isIncoming && callData.createdAt) {
        const now = Date.now();
        const diff = now - callData.createdAt;
        if (diff > 60000) { // If call is older than 60 seconds, ignore it
           console.log("Stale call detected, cleaning up...");
           navigate('/dashboard');
           return;
        }
      }

      const targetId = callData.targetUid;
      if (targetId) {
        try {
          const targetDoc = await getDoc(doc(db, 'users', targetId));
          if (targetDoc.exists()) setTargetUserData(targetDoc.data());
        } catch (e) {}
      } else if (callData.isGroupCall && callData.groupId) {
        setTargetUserData({ fullName: callData.groupName || 'Group Call', photoURL: 'https://cdn-icons-png.flaticon.com/512/615/615075.png' });
      }

      let callDocId;
      if (callData.isGroupCall && callData.groupId) {
        callDocId = callData.callId || `call_${callData.groupId}`;
      } else {
        callDocId = callData.callId || `call_${getRoomId(userUid, targetId)}`;
      }
      
      activeCallIdRef.current = callDocId;
      const safeName = `nexora_${callDocId}`.replace(/[^a-zA-Z0-9]/g, '_');
      setRoomName(safeName);

      if (callData.isGroupCall && callData.groupId) {
        try {
          const groupSnap = await getDoc(doc(db, 'groups', callData.groupId));
          if (groupSnap.exists()) {
            const groupData = groupSnap.data();
            setGroupInfo({ name: groupData.name || callData.groupName || 'Group Call', photoURL: groupData.photoURL || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200' });
            setTargetUserData({ fullName: groupData.name || callData.groupName || 'Group Call', photoURL: groupData.photoURL || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200' });
          }
        } catch (e) {}
      }

      if (!callData.isIncoming) {
        try {
          if (callData.isGroupCall && callData.groupMembers) {
            setCallStatus('Waiting for others...');
            await setDoc(doc(db, 'calls', callDocId), { callerUid: userUid, isGroupCall: true, groupId: callData.groupId, callType: callData.callType || 'audio', status: 'active', createdAt: Date.now() });

            const promises = callData.groupMembers.map(mUid => {
              if (mUid !== userUid) {
                const memberCallDocId = `call_${callData.groupId}_${mUid}`;
                return setDoc(doc(db, 'calls', memberCallDocId), { callerUid: userUid, receiverUid: mUid, callType: callData.callType || 'audio', status: 'ringing', createdAt: Date.now(), isGroupCall: true, groupId: callData.groupId, sharedCallId: callDocId });
              }
              return Promise.resolve();
            });
            await Promise.all(promises);

          } else if (targetId) {
            startDialingTone(); // Caller hears dial tone
            setCallStatus('Ringing...');
            await setDoc(doc(db, 'calls', callDocId), { callerUid: userUid, receiverUid: targetId, callType: callData.callType || 'audio', status: 'ringing', createdAt: Date.now() });
          }
        } catch (e) {}
      } else {
        // Incoming Connecting Phase - Play Toot Toot
        startDialingTone(); 
      }

      // If this is a 1:1 audio call, prefer WebRTC direct peer connection (WhatsApp-like)
      if (!callData.isGroupCall && (callData.callType || 'audio') === 'audio') {
        // Caller will create offer later when needed; acceptor will set up when accepted
      }

      unsubscribeCallDoc = onSnapshot(doc(db, 'calls', callDocId), (snapshot) => {
        if (!snapshot.exists()) {
          // MISSED CALL BUG FIX: If caller deleted document (cancelled), receiver exits
          if (!callData.isGroupCall) handleEndCallLocal(); 
        } else {
          const data = snapshot.data();
          if (data.status === 'ended' || data.status === 'rejected' || data.status === 'missed' || data.status === 'cancelled') {
            if (!callData.isGroupCall) handleEndCallLocal();
          } else if (data.status === 'accepted') {
            stopDialingTone();
            if (callStatus !== 'Connected') setCallStatus('Connecting...');
            // If accepted and 1:1 audio, start WebRTC flow
            if (!callData.isGroupCall && (callData.callType || 'audio') === 'audio') {
              const isCaller = !callData.isIncoming;
              startWebRTCCall(callDocId, isCaller).catch(() => {});
            }
          }
        }
      });
    };

    initCall();

    return () => {
      stopDialingTone();
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (unsubscribeCallDoc) unsubscribeCallDoc();
    };
  }, []);

  const handleApiReady = (apiObj) => {
    jitsiApiRef.current = apiObj;

    apiObj.on('participantJoined', (participant) => {
      isConnectedRef.current = true;
      stopDialingTone();
      setCallStatus('Connected');
      
      setParticipants(prev => {
        if(prev.find(p => p.id === participant.id)) return prev;
        return [...prev, {
          id: participant.id,
          displayName: participant.displayName || 'Member',
          avatarUrl: participant.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300',
          isSpeaking: false
        }];
      });

      if (callDuration === 0) startTimer();
    });

    apiObj.on('participantLeft', (participant) => {
      setParticipants(prev => prev.filter(p => p.id !== participant.id));
      setSpeakingParticipants(prev => prev.filter(id => id !== participant.id));
      if (jitsiApiRef.current) {
        const pCount = jitsiApiRef.current.getNumberOfParticipants();
        if (pCount <= 1) {
          if (!callData.isGroupCall) handleEndCallLocal();
          else {
            setCallStatus('Waiting for others...');
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            setCallDuration(0);
          }
        }
      }
    });

    apiObj.on('participantAudioLevelChanged', (event) => {
      const participantId = event.participantId || event.id || event.participant?.id;
      const audioLevel = event.audioLevel || event.level || 0;
      if (!participantId) return;

      setSpeakingParticipants(prev => {
        const isActive = audioLevel > 0.08;
        if (isActive && !prev.includes(participantId)) return [...prev, participantId];
        if (!isActive && prev.includes(participantId)) return prev.filter(id => id !== participantId);
        return prev;
      });
    });

    apiObj.on('screenSharingStatusChanged', (event) => {
      setIsScreenShared(Boolean(event.on));
    });

    apiObj.on('videoConferenceLeft', () => handleEndCallLocal());
  };

  // ---------- WebRTC 1:1 Audio Implementation (Firestore signaling) ----------
  const startWebRTCCall = async (callDocId, isCaller) => {
    if (pcRef.current) return;
    const servers = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
    const pc = new RTCPeerConnection(servers);
    pcRef.current = pc;

    // local audio
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
    } catch (e) {
      console.warn('getUserMedia failed', e); return;
    }

    // remote audio element
    if (!remoteAudioRef.current) {
      const a = document.createElement('audio');
      a.autoplay = true; a.playsInline = true; a.id = 'nexora-remote-audio';
      document.body.appendChild(a);
      remoteAudioRef.current = a;
    }

    pc.ontrack = (event) => {
      try { remoteAudioRef.current.srcObject = event.streams[0]; } catch(e) { }
    };

    // ICE candidates -> Firestore
    const callRef = doc(db, 'calls', callDocId);
    const callerCandidatesCollection = collection(callRef, 'callerCandidates');
    const calleeCandidatesCollection = collection(callRef, 'calleeCandidates');

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      const cand = event.candidate.toJSON();
      if (isCaller) {
        addDoc(callerCandidatesCollection, cand).catch(()=>{});
      } else {
        addDoc(calleeCandidatesCollection, cand).catch(()=>{});
      }
    };

    const callSnapshotUnsub = onSnapshot(callRef, async (snap) => {
      const data = snap.data();
      if (!data) return;
      if (isCaller && data.answer) {
        const answer = data.answer;
        await pc.setRemoteDescription(answer);
      }
      if (!isCaller && data.offer) {
        const offer = data.offer;
        await pc.setRemoteDescription(offer);
        const answerDesc = await pc.createAnswer();
        await pc.setLocalDescription(answerDesc);
        await updateDoc(callRef, { answer: pc.localDescription }).catch(()=>{});
      }
    });

    // Listen for remote ICE candidates
    const callerCandUnsub = onSnapshot(callerCandidatesCollection, (snap) => {
      snap.docChanges().forEach(change => {
        if (change.type === 'added') {
          const cand = change.doc.data();
          pc.addIceCandidate(new RTCIceCandidate(cand)).catch(()=>{});
        }
      });
    });
    const calleeCandUnsub = onSnapshot(calleeCandidatesCollection, (snap) => {
      snap.docChanges().forEach(change => {
        if (change.type === 'added') {
          const cand = change.doc.data();
          pc.addIceCandidate(new RTCIceCandidate(cand)).catch(()=>{});
        }
      });
    });

    if (isCaller) {
      const offerDesc = await pc.createOffer();
      await pc.setLocalDescription(offerDesc);
      await updateDoc(callRef, { offer: pc.localDescription }).catch(async () => {
        // fallback to setDoc if doc doesn't exist yet
        await setDoc(callRef, { offer: pc.localDescription }, { merge: true });
      });
    }

    // cleanup handler when call ends
    const cleanup = () => {
      try { callSnapshotUnsub(); callerCandUnsub(); calleeCandUnsub(); } catch(e) {}
      try { pc.getSenders().forEach(s => s.track && s.track.stop()); } catch(e){}
      try { pc.close(); } catch(e){}
      pcRef.current = null;
      try { if (remoteAudioRef.current) { remoteAudioRef.current.pause(); remoteAudioRef.current.srcObject = null; remoteAudioRef.current.remove(); remoteAudioRef.current = null; } } catch(e){}
      try { localStreamRef.current && localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; } catch(e){}
    };

    // attach cleanup to activeCallIdRef watcher (handled elsewhere on hangup)
    // return cleanup so callers can use it if needed
    return cleanup;
  };

  // Force Volume Settings (25% or 100%)
  const applyVolumeConfig = (speakerOn) => {
    try {
      const targetVolume = speakerOn ? 1.0 : 0.2;
      const audioElements = document.querySelectorAll('audio, video');
      audioElements.forEach(el => { el.volume = targetVolume; });
      if (window.nexoraDialingAudio) {
        window.nexoraDialingAudio.volume = speakerOn ? 1.0 : 0.25;
      }
      if (window.nexoraIncomingAudio) {
        window.nexoraIncomingAudio.volume = speakerOn ? 1.0 : 1.0;
      }
    } catch (e) {}
  };

  useEffect(() => {
    // Keep enforcing volume as new participants join and audio tags are created
    const volInterval = setInterval(() => { applyVolumeConfig(isSpeakerOn); }, 1500);
    return () => clearInterval(volInterval);
  }, [isSpeakerOn]);

  const toggleMute = () => {
    const nextState = !isMuted;
    setIsMuted(nextState);
    if (jitsiApiRef.current) { try { jitsiApiRef.current.executeCommand('toggleAudio'); } catch (e) {} }
  };

  const toggleVideo = () => {
    const nextState = !isVideoOff;
    setIsVideoOff(nextState);
    if (jitsiApiRef.current) { try { jitsiApiRef.current.executeCommand('toggleVideo'); } catch (e) {} }
  };

  const toggleSpeaker = () => {
    const nextSpeakerState = !isSpeakerOn;
    setIsSpeakerOn(nextSpeakerState);
    applyVolumeConfig(nextSpeakerState);
    if (window.nexoraDialingAudio) {
      window.nexoraDialingAudio.volume = nextSpeakerState ? 1.0 : 0.25;
    }
  };

  const handleStartScreenShare = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
   
      return;
    }

    try {
      await navigator.mediaDevices.getDisplayMedia({ video: true });
      if (jitsiApiRef.current) {
        jitsiApiRef.current.executeCommand('toggleShareScreen');
      }
      setShowMoreActions(false);
    } catch (e) {
      console.warn('Screen sharing permission denied or cancelled.', e);
    }
  };

  const handleSendCallInvitations = async () => {
    if (selectedInviteUids.length === 0) return;
    const promises = selectedInviteUids.map(targetId => {
      const inviteCallDocId = callData.isGroupCall ? `call_${callData.groupId}_${targetId}_invite` : `call_${Date.now()}_${targetId}`;
      return setDoc(doc(db, 'calls', inviteCallDocId), { callerUid: currentUid, receiverUid: targetId, callType: callData.callType || 'audio', status: 'ringing', createdAt: Date.now(), isGroupCall: callData.isGroupCall || false, groupId: callData.groupId || null, sharedCallId: activeCallIdRef.current });
    });
    try {
      await Promise.all(promises);
      alert(`${selectedInviteUids.length} জন ইউজারকে ইনভাইট পাঠানো হয়েছে!`);
      setShowInviteModal(false); setSelectedInviteUids([]);
    } catch (e) {}
  };

  const toggleSelectInviteUser = (uid) => {
    if (selectedInviteUids.includes(uid)) setSelectedInviteUids(selectedInviteUids.filter(id => id !== uid));
    else setSelectedInviteUids([...selectedInviteUids, uid]);
  };

  const handleHeaderBack = () => {
    setIsMinimized(true);
    navigate('/dashboard');
  };

  const handleEndCallLocal = async () => {
    stopDialingTone();
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    
    if (callData.isGroupCall && activeCallIdRef.current && jitsiApiRef.current) {
      try {
        const others = jitsiApiRef.current.getParticipantsInfo();
        if (others.length === 0) {
          updateDoc(doc(db, 'calls', activeCallIdRef.current), { status: 'ended' }).catch(()=>{});
          setTimeout(() => { deleteDoc(doc(db, 'calls', activeCallIdRef.current)).catch(() => {}); }, 2000);
        }
      } catch(e) {}
    }

    if (isConnectedRef.current && !callData.isGroupCall && !callData.isIncoming) {
      await createCallLog('call_ended', callDuration);
    }

    if (jitsiApiRef.current) { try { jitsiApiRef.current.executeCommand('hangup'); } catch (e) {} }
    navigate('/dashboard');
  };

  const handleEndCall = async () => {
    stopDialingTone();
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    if (activeCallIdRef.current) {
      try {
        if (!callData.isGroupCall) {
          await updateDoc(doc(db, 'calls', activeCallIdRef.current), { status: 'ended' }).catch(()=>{});
          setTimeout(() => { deleteDoc(doc(db, 'calls', activeCallIdRef.current)).catch(() => {}); }, 2000);
          if(isConnectedRef.current) await createCallLog('call_ended', callDuration);
        } else {
          if (jitsiApiRef.current && jitsiApiRef.current.getNumberOfParticipants() <= 1) {
            await updateDoc(doc(db, 'calls', activeCallIdRef.current), { status: 'ended' }).catch(()=>{});
            setTimeout(() => { deleteDoc(doc(db, 'calls', activeCallIdRef.current)).catch(() => {}); }, 2000);
          }
        }
      } catch (e) {}
    }

    if (jitsiApiRef.current) { try { jitsiApiRef.current.executeCommand('hangup'); } catch (e) {} }
    navigate('/dashboard');
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    isDraggingRef.current = false;
    dragOffsetRef.current = { x: touch.clientX - bubblePos.x, y: touch.clientY - bubblePos.y };
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    isDraggingRef.current = true;
    const newX = Math.min(Math.max(10, touch.clientX - dragOffsetRef.current.x), window.innerWidth - 80);
    const newY = Math.min(Math.max(10, touch.clientY - dragOffsetRef.current.y), window.innerHeight - 80);
    setBubblePos({ x: newX, y: newY });
  };

  const handleBubbleClick = () => {
    if (!isDraggingRef.current) { setIsMinimized(false); navigate('/call', { state: callData }); }
  };

  const localContacts = getLocalContacts();
  const isAudioOnly = callData.callType === 'audio';

  const filteredInviteContacts = allUsersList.filter(u => {
    const normP = normalizePhone(u.phoneNumber);
    const sName = localContacts[normP] || u.fullName || normP;
    return sName.toLowerCase().includes(searchContactQuery.toLowerCase()) || normP.includes(searchContactQuery);
  });

  return (
    <div style={styles.container}>
      <style>{`
        /* ANTI-SELECTION & NO BLUE BOX FIX */
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-tap-highlight-color: transparent; outline: none; }
        body, html, #root { margin: 0; padding: 0; height: 100dvh; width: 100vw; overflow: hidden; background: #fafafa; user-select: none; -webkit-user-select: none; }
        input { user-select: auto; -webkit-user-select: auto; }
        
        .call-card { width: 100%; max-width: 480px; height: 100dvh; background: #ffffff; color: #0f172a; display: flex; flex-direction: column; justify-content: space-between; position: relative; overflow: hidden; box-shadow: 0 0 40px rgba(0,0,0,0.04); }
        @media (min-width: 768px) { .call-card { height: 92vh; border-radius: 36px; border: 1px solid #f1f5f9; } }
        
        .call-header { padding: max(16px, env(safe-area-inset-top)) 20px 16px; display: flex; align-items: center; justify-content: space-between; z-index: 20; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(12px); border-bottom: 1px solid #f1f5f9; }
        .header-btn { width: 40px; height: 40px; border-radius: 50%; background: #f0f2f5; border: none; color: #0f172a; display: flex; align-items: center; justify-content: center; cursor: pointer; }
        
        .floating-control-bar { position: absolute; bottom: max(30px, env(safe-area-inset-bottom)); left: 16px; right: 16px; background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(16px); border-radius: 36px; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; border: 1px solid #f1f5f9; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08); z-index: 30; }
        .dock-btn { width: 48px; height: 48px; border-radius: 50%; background: #f0f2f5; border: none; color: #1e293b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease; }
        .dock-btn.active-off { background: #ef4444 !important; color: #ffffff !important; }
        .dock-btn.active-on { background: #62b875 !important; color: #ffffff !important; }
        .end-call-dock-btn { width: 56px; height: 56px; border-radius: 50%; background: #ef4444; border: none; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 8px 20px rgba(239, 68, 68, 0.35); }
        
        .avatar-pulse { animation: pulse 2s infinite; }
        @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(98, 184, 117, 0.4); } 70% { box-shadow: 0 0 0 22px rgba(98, 184, 117, 0); } 100% { box-shadow: 0 0 0 0 rgba(98, 184, 117, 0); } }
        
        .jitsi-holder { flex: 1; width: 100%; height: 100%; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: #ffffff; z-index: 5; }
        html[data-nexora-theme="dark"] #root .jitsi-holder iframe { filter: invert(1) hue-rotate(180deg); }
        .frequency-bubble-wrapper { position: fixed; z-index: 999999; touch-action: none; display: flex; align-items: center; justify-content: center; }
        .frequency-ring { position: absolute; width: 72px; height: 72px; border-radius: 50%; border: 2px solid #62b875; opacity: 0; animation: waveRipple 2s infinite linear; pointer-events: none; }
        .ring-delay-1 { animation-delay: 0s; } .ring-delay-2 { animation-delay: 0.6s; } .ring-delay-3 { animation-delay: 1.2s; }
        @keyframes waveRipple { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(1.85); opacity: 0; } }
        .call-bubble-inner { position: relative; width: 72px; height: 72px; border-radius: 50%; background: #ffffff; border: 3px solid #62b875; box-shadow: 0 10px 30px rgba(98, 184, 117, 0.35); display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: grab; z-index: 10; }
        
        .invite-modal-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(6px); z-index: 100; display: flex; align-items: flex-end; justify-content: center; }
        .invite-card-sheet { width: 100%; max-width: 480px; background: #ffffff; border-radius: 28px 28px 0 0; padding: 24px; max-height: 80vh; overflow-y: auto; color: #0f172a; }

        .audio-grid-container { flex: 1; display: flex; flex-wrap: wrap; gap: 12px; padding: 20px; align-content: center; justify-content: center; z-index: 15; background: #ffffff; padding-bottom: 90px; }
        .audio-grid-card { flex: 1 1 40%; max-width: 200px; aspect-ratio: 3/4; background: #f1f5f9; border-radius: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 10px 20px rgba(0,0,0,0.05); position: relative; overflow: hidden; }
        .audio-grid-avatar { width: 70px; height: 70px; border-radius: 50%; background: #e2e8f0; display: flex; justify-content: center; align-items: center; font-size: 30px; margin-bottom: 12px; border: 3px solid #62b875; }
        .audio-grid-name { font-size: 14px; font-weight: 700; color: #0f172a; text-align: center; padding: 0 8px; }
        .audio-grid-card.speaking { transform: scale(1.04); box-shadow: 0 14px 26px rgba(98, 184, 117, 0.2); }
        .voice-activity { position: absolute; top: 12px; right: 12px; width: 18px; height: 18px; border-radius: 50%; background: radial-gradient(circle, rgba(34,197,94,0.9) 0%, rgba(34,197,94,0.15) 70%); animation: pulseSpeak 1s infinite; }
        @keyframes pulseSpeak { 0% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.35); opacity: 0.55; } 100% { transform: scale(1); opacity: 0.9; } }
        .more-action-menu { position: absolute; bottom: 85px; left: 16px; width: 180px; background: #ffffff; border-radius: 18px; border: 1px solid #e2e8f0; box-shadow: 0 16px 40px rgba(15,23,42,0.12); display: flex; flex-direction: column; gap: 8px; padding: 10px; z-index: 40; }
        .menu-item { width: 100%; text-align: left; padding: 10px 14px; border-radius: 14px; background: #f8fafc; border: none; color: #0f172a; font-weight: 700; cursor: pointer; }
        .menu-item:hover { background: #e2e8f0; }
      `}</style>

      {showInviteModal && (
        <div className="invite-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-card-sheet" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800' }}>Invite People to Call</h3>
                <span style={{ fontSize: '12px', color: '#62b875', fontWeight: '700' }}>{selectedInviteUids.length} selected</span>
              </div>
              <button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            <input type="text" placeholder="Search Contact Name or Phone..." value={searchContactQuery} onChange={e => setSearchContactQuery(e.target.value)} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '14px', marginBottom: '14px', outline: 'none', fontSize: '14px' }} />
            <div style={{ maxHeight: '280px', overflowY: 'auto', marginBottom: '16px' }}>
              {filteredInviteContacts.map(u => {
                const normP = normalizePhone(u.phoneNumber);
                const sName = localContacts[normP] || u.fullName || normP;
                const isSelected = selectedInviteUids.includes(u.uid);
                return (
                  <div key={u.uid} onClick={() => toggleSelectInviteUser(u.uid)} style={{ padding: '10px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <img src={u.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150"} alt="User" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>{sName}</h4>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>{u.phoneNumber}</span>
                      </div>
                    </div>
                    <input type="checkbox" checked={isSelected} readOnly style={{ width: '18px', height: '18px' }} />
                  </div>
                );
              })}
            </div>
            <button onClick={handleSendCallInvitations} style={{ width: '100%', padding: '14px', background: '#62b875', color: 'white', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '15px', cursor: 'pointer' }}>Send Call Invitation ({selectedInviteUids.length})</button>
          </div>
        </div>
      )}

      {isMinimized && (
        <div className="frequency-bubble-wrapper" style={{ left: `${bubblePos.x}px`, top: `${bubblePos.y}px` }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onClick={handleBubbleClick}>
          <div className="frequency-ring ring-delay-1"></div><div className="frequency-ring ring-delay-2"></div><div className="frequency-ring ring-delay-3"></div>
          <div className="call-bubble-inner">
            <img src={targetUserData?.photoURL || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300"} alt="Caller" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
            <span style={{ fontSize: '9px', fontWeight: '800', color: '#62b875', marginTop: '2px' }}>{callStatus === 'Connected' ? formatTime(callDuration) : 'Calling...'}</span>
          </div>
        </div>
      )}

      <div className="call-card" style={{ display: isMinimized ? 'none' : 'flex' }}>
        <div className="call-header">
          <button onClick={handleHeaderBack} className="header-btn" title="Back to Dashboard"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg></button>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0, color: '#0f172a' }}>{targetUserData?.fullName || 'Nexora User'}</h3>
            <span style={{ fontSize: '12px', color: '#62b875', fontWeight: '700' }}>{callStatus === 'Connected' ? formatTime(callDuration) : callStatus}</span>
          </div>
          <button onClick={() => setShowInviteModal(true)} className="header-btn" title="Add Person"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="17" y1="11" x2="23" y2="11"/></svg></button>
        </div>

        {/* Audio Call UI (Single or Grid for 3+ people) */}
        {(isVideoOff || isAudioOnly) && (
          <div style={{ flex: 1, background: '#ffffff', paddingBottom: '90px', display: 'flex', flexDirection: 'column' }}>
            {/* Header info for Group Calls ONLY */}
            {callData.isGroupCall && (
              <div style={{ padding: '20px 20px 0 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src={groupInfo.photoURL || targetUserData?.photoURL || "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=200"} alt="Group" style={{ width: '52px', height: '52px', borderRadius: '18px', objectFit: 'cover', border: '2px solid #62b875' }} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>{groupInfo.name || targetUserData?.fullName || 'Nexora Call'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Group Audio</div>
                </div>
              </div>
            )}

            {participants.length <= 1 ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, minHeight: '320px' }}>
                <div className="avatar-pulse" style={{ borderRadius: '50%' }}>
                  <img src={targetUserData?.photoURL || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300"} alt="Caller" style={{ width: '130px', height: '130px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #62b875' }} />
                </div>
                <h4 style={{ marginTop: '20px', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>{targetUserData?.fullName || 'Nexora User'}</h4>
                <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{callData.callType === 'audio' ? 'Voice Call' : 'Video Off'}</p>
              </div>
            ) : (
              <div className="audio-grid-container">
                <div className={`audio-grid-card ${speakingParticipants.includes('me') ? 'speaking' : ''}`}>
                  {speakingParticipants.includes('me') && <div className="voice-activity" />}
                  <img src={currentUserData?.photoURL || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300"} alt="You" style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', marginBottom: '12px' }} />
                  <div className="audio-grid-name">You</div>
                </div>
                {participants.map(p => (
                  <div key={p.id} className={`audio-grid-card ${speakingParticipants.includes(p.id) ? 'speaking' : ''}`}>
                    {speakingParticipants.includes(p.id) && <div className="voice-activity" />}
                    <img src={p.avatarUrl} alt={p.displayName} style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover', marginBottom: '12px' }} />
                    <div className="audio-grid-name">{p.displayName || 'Member'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Jitsi Holder (Visible only for Video Calls) */}
        <div className="jitsi-holder" style={{ opacity: (isVideoOff || isAudioOnly) ? 0 : 1, pointerEvents: (isVideoOff || isAudioOnly) ? 'none' : 'auto' }}>
          {roomName && currentUid && (
            <JitsiMeeting
              key={roomName}
              domain="meet.element.io"
              roomName={roomName}
              containerStyle={{ width: '100%', height: '100%' }}
              configOverwrite={{
                prejoinConfig: { enabled: false },
                prejoinPageEnabled: false,
                enableWelcomePage: false,
                disableDeepLinking: true,
                requireDisplayName: false,
                startWithAudioMuted: false,
                startWithVideoMuted: isAudioOnly,
                p2p: { enabled: false }, 
                disableAudioLevels: true,
                enableNoAudioDetection: false,
                disable1On1Mode: false,
                resolution: 360,
                disableSimulcast: true,
                enableLayerSuspension: true,
                toolbarButtons: []
              }}
              interfaceConfigOverwrite={{
                SHOW_JITSI_WATERMARK: false,
                SHOW_WATERMARK_FOR_GUESTS: false,
                DEFAULT_BACKGROUND: '#ffffff',
                MOBILE_APP_PROMO: false
              }}
              userInfo={{ displayName: currentUserData?.fullName || 'Nexora User' }}
              onApiReady={handleApiReady}
              getIFrameRef={(iframeRef) => {
                iframeRef.style.height = '100%';
                iframeRef.style.width = '100%';
                iframeRef.style.border = 'none';
              }}
            />
          )}
        </div>

        {/* Improved Control Bar */}
        <div className="floating-control-bar">
          <button onClick={() => setShowMoreActions(prev => !prev)} className="dock-btn" title="More Actions">···</button>
          {showMoreActions && (
            <div className="more-action-menu">
              <button onClick={handleStartScreenShare} className="menu-item">Share Screen</button>
              <button onClick={() => { toggleSpeaker(); setShowMoreActions(false); }} className="menu-item">{isSpeakerOn ? 'Speaker Off' : 'Speaker On'}</button>
              <button onClick={() => { toggleMute(); setShowMoreActions(false); }} className="menu-item">{isMuted ? 'Unmute' : 'Mute'}</button>
            </div>
          )}
          
          <button onClick={toggleVideo} className={`dock-btn ${isVideoOff ? 'active-off' : ''}`} title="Camera Toggle">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="3"/></svg>
          </button>

          <button onClick={toggleMute} className={`dock-btn ${isMuted ? 'active-off' : ''}`} title="Mute Mic">
            {isMuted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            )}
          </button>

          <button onClick={toggleSpeaker} className={`dock-btn ${isSpeakerOn ? 'active-on' : ''}`} title="Speaker">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
          </button>

          <button onClick={handleEndCall} className="end-call-dock-btn" title="End Call">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3" style={{ transform: 'rotate(135deg)' }}>
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.79 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = { container: { minHeight: '100vh', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' } };

export default CallScreen;